import {
  addDays,
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isWeekend,
  max,
  min,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

export const GANTT_VIEW_MODE = {
  MONTH: 'month',
  WEEK: 'week',
}

const BUSINESS_TIMEZONE = 'Asia/Kuala_Lumpur'
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/
const datePartsFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BUSINESS_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const buildLocalDate = (year, month, day) => {
  const y = Number(year)
  const m = Number(month)
  const d = Number(day)

  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null

  return startOfDay(new Date(y, m - 1, d))
}

const toBusinessDateFromTimestamp = (value) => {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null

  const parts = datePartsFormatter.formatToParts(parsed)
  const year = parts.find((part) => part.type === 'year')?.value
  const month = parts.find((part) => part.type === 'month')?.value
  const day = parts.find((part) => part.type === 'day')?.value

  return buildLocalDate(year, month, day)
}

export const toSafeDate = (value) => {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) return startOfDay(value)

  try {
    if (typeof value === 'string') {
      const dateOnlyMatch = value.match(DATE_ONLY_PATTERN)
      if (dateOnlyMatch) {
        return buildLocalDate(dateOnlyMatch[1], dateOnlyMatch[2], dateOnlyMatch[3])
      }

      const businessDate = toBusinessDateFromTimestamp(value)
      if (businessDate) return businessDate

      return null
    }

    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed)
  } catch {
    return null
  }
}

export const getRangeForView = (anchorDate, viewMode) => {
  const safeAnchor = toSafeDate(anchorDate) || startOfDay(new Date())

  if (viewMode === GANTT_VIEW_MODE.WEEK) {
    return {
      rangeStart: startOfWeek(safeAnchor, { weekStartsOn: 1 }),
      rangeEnd: endOfWeek(safeAnchor, { weekStartsOn: 1 }),
    }
  }

  return {
    rangeStart: startOfMonth(safeAnchor),
    rangeEnd: endOfMonth(safeAnchor),
  }
}

export const buildTimelineColumns = (anchorDate, viewMode) => {
  const { rangeStart, rangeEnd } = getRangeForView(anchorDate, viewMode)
  const today = startOfDay(new Date())
  const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd })

  return {
    rangeStart,
    rangeEnd,
    columns: days.map((date) => ({
      key: format(date, 'yyyy-MM-dd'),
      date,
      label: viewMode === GANTT_VIEW_MODE.WEEK ? format(date, 'd') : format(date, 'd'),
      subLabel: format(date, 'EEE'),
      isToday: isSameDay(date, today),
      isWeekend: isWeekend(date),
    })),
  }
}

export const shiftAnchorDate = (anchorDate, viewMode, direction) => {
  const safeAnchor = toSafeDate(anchorDate) || startOfDay(new Date())
  if (viewMode === GANTT_VIEW_MODE.WEEK) {
    return addWeeks(safeAnchor, direction)
  }
  return addMonths(safeAnchor, direction)
}

export const getVisibleRangeLabel = (anchorDate, viewMode) => {
  const safeAnchor = toSafeDate(anchorDate) || startOfDay(new Date())

  if (viewMode === GANTT_VIEW_MODE.WEEK) {
    const { rangeStart, rangeEnd } = getRangeForView(safeAnchor, viewMode)
    if (format(rangeStart, 'MMM yyyy') === format(rangeEnd, 'MMM yyyy')) {
      return `${format(rangeStart, 'd')} - ${format(rangeEnd, 'd MMM yyyy')}`
    }
    return `${format(rangeStart, 'd MMM')} - ${format(rangeEnd, 'd MMM yyyy')}`
  }

  return format(safeAnchor, 'MMMM yyyy')
}

export const buildBarLayout = ({ start, end, rangeStart, rangeEnd, columnCount }) => {
  const safeStart = toSafeDate(start)
  const safeEnd = toSafeDate(end)

  if (!safeStart || !safeEnd || safeStart > safeEnd || !columnCount) return null
  if (safeEnd < rangeStart || safeStart > rangeEnd) return null

  const clippedStart = max([safeStart, rangeStart])
  const clippedEnd = min([safeEnd, rangeEnd])

  const offsetStart = Math.max(0, Math.round((clippedStart - rangeStart) / 86400000))
  const offsetEnd = Math.max(0, Math.round((clippedEnd - rangeStart) / 86400000))
  const span = Math.max(1, offsetEnd - offsetStart + 1)

  return {
    gridColumnStart: offsetStart + 1,
    gridColumnEnd: offsetStart + span + 1,
    startsBefore: safeStart < rangeStart,
    endsAfter: safeEnd > rangeEnd,
  }
}

export const expandDateRangeByOneDay = (date) => addDays(toSafeDate(date) || new Date(), 1)
